import type {
	AIResponsePayload,
	BaseItemSchema,
	TranscriptTurn,
} from "@memorang/types/src/session/new-session";

import {
	SCENARIO_REGISTRY,
	isProethicsScenarioId,
} from "../data/scenario-registry";
import {
	type ReviewRunSeed,
	useProethicsFlowStore,
} from "../hooks/useProethicsFlowStore";
import type {
	ProethicsScenarioId,
	SummativeResult,
	TranscriptMessage,
} from "../types";

/** No `audioUrl` means TTS failed/timed out/aborted; replays as `unavailable`, never as an unresolvable spinner. */
export const reviewTranscriptFromPersisted = (
	turns: TranscriptTurn[],
	scenarioId: ProethicsScenarioId,
): TranscriptMessage[] => {
	const avatarRole = SCENARIO_REGISTRY[scenarioId].avatarRole;
	return turns.map((turn, index) => {
		const base = {
			id: `review-${scenarioId}-${index}`,
			text: turn.text,
		};
		if (turn.role === "user") {
			return { ...base, role: "mentor" as const };
		}
		return {
			...base,
			role: avatarRole,
			...(turn.audioUrl ? { audioUrl: turn.audioUrl } : {}),
			audioStatus: turn.audioUrl
				? ("ready" as const)
				: ("unavailable" as const),
		};
	});
};

/**
 * Keeps the persisted ORDER (matches live `summative.criteria`) instead of
 * re-mapping by name: ResultsScreen index-matches `criteria[i]` to
 * `reflectSkills[i]`, and persisted names are truncated SSE names, so a name
 * lookup misses. Only `score`/`reason` are read here; the rest comes from
 * `reflectSkills` in ResultsScreen.
 */
export const reviewSummativeFromPersisted = (
	aiResponse: AIResponsePayload,
): SummativeResult => {
	const criteria = (aiResponse.subskillAssessments ?? []).map((assessment) => ({
		name: assessment.name ?? "",
		description: "",
		reason: assessment.reason ?? "",
		score: assessment.score ?? 0,
	}));
	return { criteria, feedback: aiResponse.feedback ?? "" };
};

/** Seeds the flow store's per-scenario runs from persisted `answer.aiResponse` so sim + reflect items render read-only in review. */
export const hydrateProethicsReview = (items: BaseItemSchema[]) => {
	const seeds: Partial<Record<ProethicsScenarioId, ReviewRunSeed>> = {};

	for (const item of items) {
		const scenarioId = item.metadata?.scenarioId;
		const aiResponse = item.answer?.aiResponse;

		if (!(isProethicsScenarioId(scenarioId) && aiResponse)) {
			continue;
		}

		seeds[scenarioId] = {
			transcript: reviewTranscriptFromPersisted(
				aiResponse.transcript ?? [],
				scenarioId,
			),
			summative: reviewSummativeFromPersisted(aiResponse),
			threadId: aiResponse.ai_thread_id || null,
		};
	}

	if (Object.keys(seeds).length > 0) {
		useProethicsFlowStore.getState().hydrateReviewRuns(seeds);
	}
};
